import React from 'react';
import Axios from 'axios';
import { useMutation } from '@tanstack/react-query';
import SPAButton from '@/components/admin/SysadminSPA/shared/SPAButton';
import SPADialog from '@/components/admin/SysadminSPA/shared/SPADialog';
import { useSPAToast } from '@/components/admin/SysadminSPA/shared/SPAToast';
import { toggleVisibilityUrl } from './slideUrls';
import type { SlideRow, SlideTableContext } from './types';

interface ToggleVisibilityDialogProps {
  context: SlideTableContext;
  onOpenChange: (open: boolean) => void;
  onToggled: (slide: SlideRow) => void;
  open: boolean;
  slide: SlideRow;
}

// The toggle route is the same GET the old link used; it flips the flag and redirects, and the row
// flips locally once it comes back.
const ToggleVisibilityDialog = ({
  context, onOpenChange, onToggled, open, slide,
}: ToggleVisibilityDialogProps) => {
  const spaToast = useSPAToast();
  const verb = slide.hidden ? 'Show' : 'Hide';
  const toggle = useMutation({
    mutationFn: () => Axios.get(toggleVisibilityUrl(slide, context)),
    onSuccess: () => {
      onOpenChange(false);
      onToggled(slide);
      spaToast.success(slide.hidden ? `${slide.title} is visible again.` : `Hid ${slide.title}.`);
    },
  });

  const handleOpenChange = (next: boolean) => {
    if (!next) toggle.reset();
    onOpenChange(next);
  };

  return (
    <SPADialog open={open} onOpenChange={handleOpenChange}>
      <SPADialog.Content size="sm">
        <SPADialog.Header>
          <SPADialog.Title>{verb} {slide.title}?</SPADialog.Title>
          <SPADialog.CloseButton />
        </SPADialog.Header>
        <SPADialog.Body className="tw-flex tw-flex-col tw-gap-3">
          <SPADialog.Description>
            {slide.hidden
              ? 'Teachers and students see this slide again.'
              : 'Teachers and students stop seeing this slide until you show it again.'}
          </SPADialog.Description>
          {toggle.isError && (
            <p className="tw-m-0 tw-text-sm tw-text-spa-error" role="alert">The slide could not be changed. Try again.</p>
          )}
        </SPADialog.Body>
        <SPADialog.Footer>
          <SPADialog.Close asChild>
            <SPAButton>Cancel</SPAButton>
          </SPADialog.Close>
          <SPAButton disabled={toggle.isLoading} variant="primary" onClick={() => toggle.mutate()}>{verb}</SPAButton>
        </SPADialog.Footer>
      </SPADialog.Content>
    </SPADialog>
  );
};

export default ToggleVisibilityDialog;
